import { useEffect, useState } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';        // 🔧 ajusta si la ruta cambia
import { Models } from '../../../Utils/ModelsCarBooking';    // 🔧 ajusta si la ruta cambia

const EMPTY = {
  idChofer: 0,
  nombreExcepcion: '',
  descripcion: '',
  fechaInicio: '',
  horaInicio: '',
  horaFin: '',
  fechaFin: '',     // 👈 se mantiene en el form, pero sin input en UI
  motivo: '',
  activo: true,     // sigue existiendo pero no se muestra en la UI
  fechaCreacion: '',
};

// Endpoints + modelos para armar el nombre del chofer a partir de Persona
const endpointChofer = ENDPOINTS.chofer;
const modeloChofer = Models.chofer;

const endpointEmpleado = ENDPOINTS.empleados;
const modeloEmpleado = Models.empleados;

const endpointPersona = ENDPOINTS.personas;
const modeloPersona = Models.persona;

const endpointCorreo = ENDPOINTS.correos;
const modeloCorreo = Models.correos;

export default function Modal({
  open,
  mode = 'new',
  data = null,
  onClose,
  onSubmit,
  inline = false,
}) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({
    idChofer: '',
    nombreExcepcion: '',
    descripcion: '',
    fechaInicio: '',
    horaInicio: '',
    horaFin: '',
    motivo: '',
  });

  const [choferes, setChoferes] = useState([]);
  const [loadingChoferes, setLoadingChoferes] = useState(false);
  const [errorChoferes, setErrorChoferes] = useState(null);

  const validateField = (field, rawValue) => {
    const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    let error = '';

    switch (field) {
      case 'idChofer':
        if (!value || Number(value) <= 0) error = 'Selecciona un chofer.';
        break;
      case 'nombreExcepcion':
        if (!value) error = 'El nombre de la excepción es obligatorio.';
        break;
      case 'descripcion':
        if (!value) error = 'La descripción es obligatoria.';
        break;
      case 'fechaInicio':
        if (!value) error = 'La fecha es obligatoria.';
        break;
      case 'horaInicio':
        if (!value) error = 'La hora de inicio es obligatoria.';
        break;
      case 'horaFin':
        if (!value) error = 'La hora de fin es obligatoria.';
        break;
      case 'motivo':
        if (!value) error = 'El motivo es obligatorio.';
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  };

  const validateAll = () => {
    const fields = ['idChofer', 'nombreExcepcion', 'descripcion', 'fechaInicio', 'horaInicio', 'horaFin', 'motivo'];
    let isValid = true;
    fields.forEach(f => {
      if (!validateField(f, form[f])) isValid = false;
    });
    return isValid;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    validateField(field, value);
  };

  // Cargar datos de edición
  useEffect(() => {
    if (mode === 'edit' && data) {
      const fechaIni = (data.fechaInicio ?? '').slice(0, 10);
      const fechaFin = (data.fechaFin ?? data.fechaInicio ?? '').slice(0, 10);

      const filled = {
        idChofer: data.idChofer ?? 0,
        nombreExcepcion: data.nombreExcepcion ?? '',
        descripcion: data.descripcion ?? '',
        fechaInicio: fechaIni,
        horaInicio: data.horaInicio ?? '',
        horaFin: data.horaFin ?? '',
        fechaFin: fechaFin,
        motivo: data.motivo ?? '',
        activo: data.activo ?? true,
        fechaCreacion: data.fechaCreacion ?? '',
      };
      setForm(filled);
      validateField('idChofer', filled.idChofer);
      validateField('nombreExcepcion', filled.nombreExcepcion);
      validateField('descripcion', filled.descripcion);
      validateField('fechaInicio', filled.fechaInicio);
      validateField('horaInicio', filled.horaInicio);
      validateField('horaFin', filled.horaFin);
      validateField('motivo', filled.motivo);
    } else {
      setForm(EMPTY);
      setErrors({ idChofer: '', nombreExcepcion: '', descripcion: '', fechaInicio: '', horaInicio: '', horaFin: '', motivo: '' });
    }
  }, [mode, data]);

  // Cargar choferes + empleados + personas + correos cuando se abre el modal
  useEffect(() => {
    if (!open) return;

    let canceled = false;

    async function loadChoferes() {
      setLoadingChoferes(true);
      setErrorChoferes(null);

      try {
        // 1) Choferes
        let respChoferes = await axios.get(endpointChofer);
        let choferesNorm = (respChoferes.data || []).map(modeloChofer);

        // 2) Empleados (solo los que están ligados a choferes)
        let respEmpleados = await axios.get(endpointEmpleado);
        let empleadosNorm = (respEmpleados.data || []).map(modeloEmpleado);

        empleadosNorm = empleadosNorm.filter(emp =>
          choferesNorm.some(ch => ch.idEmpleado === emp.idEmpleado)
        );

        // 3) Personas (solo las ligadas a esos empleados)
        let respPersonas = await axios.get(endpointPersona);
        let personasNorm = (respPersonas.data || []).map(modeloPersona);

        personasNorm = personasNorm.filter(per =>
          empleadosNorm.some(emp => emp.idPersona === per.idPersona)
        );

        // 4) Correos (solo de esas personas)
        let respCorreos = await axios.get(endpointCorreo);
        let correosNorm = (respCorreos.data || []).map(modeloCorreo);

        correosNorm = correosNorm.filter(cor =>
          personasNorm.some(per => per.idPersona === cor.idPersona)
        );

        // 5) Enriquecer chofer con:
        //    - nombre = "Nombre ApPaterno ApMaterno" de Persona
        //    - correo  = correo principal (si existe)
        choferesNorm = choferesNorm.map(ch => {
          const emp = empleadosNorm.find(e => e.idEmpleado === ch.idEmpleado);
          const per = emp
            ? personasNorm.find(p => p.idPersona === emp.idPersona)
            : null;
          const cor = per
            ? correosNorm.find(c => c.idPersona === per.idPersona)
            : null;

          const fullName = per
            ? `${per.nombre || ''} ${per.apellidoPaterno || ''} ${per.apellidoMaterno || ''}`.trim()
            : (ch.nombre || '');

          return {
            ...ch,
            nombre: fullName,          // 👈 se usará para mostrar en el combo
            correo: cor?.correo || '', // opcional, por si después lo necesitas
          };
        });

        if (!canceled) {
          setChoferes(choferesNorm);
        }
      } catch (err) {
        console.error('Error cargando choferes/personas', err);
        if (!canceled) {
          setErrorChoferes('No se pudieron cargar los choferes.');
        }
      } finally {
        if (!canceled) {
          setLoadingChoferes(false);
        }
      }
    }

    loadChoferes();

    return () => {
      canceled = true;
    };
  }, [open]);

  if (!open) return null;

  const hasErrors = Object.values(errors).some((e) => !!e);
  const isSaveDisabled = !form.idChofer || !form.nombreExcepcion?.trim() || !form.descripcion?.trim() || !form.fechaInicio || !form.horaInicio || !form.horaFin || !form.motivo?.trim() || hasErrors;

  const errorStyle = {
    color: '#b91c1c',
    fontSize: '.8rem',
    display: 'block',
    marginTop: '4px',
  };

  // Etiqueta amigable para el chofer: mostrar nombre de persona; si no, fallback
  function labelChofer(c) {
    if (!c) return '';
    if (c.nombre) return c.nombre; // 👈 nombre de Persona
    const licencia = c.numeroLicencia || 'Sin licencia';
    const clase = c.claseLicencia || '';
    return `Chofer #${c.idChofer} - ${licencia}${clase ? ` (${clase})` : ''}`;
  }

  const Header = (
    <div
      className="roles-topbar"
      style={{
        marginBottom: '0.6rem',
        paddingRight: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',
      }}
    >
      <h3 className="roles-title" style={{ marginRight: 0 }}>
        {mode === 'new' ? 'Nueva excepción de turno' : 'Editar excepción de turno'}
      </h3>
      <button className="roles-btn" onClick={onClose} aria-label="Cerrar">
        ✕
      </button>
    </div>
  );

  const Body = (
    <div
      style={{
        paddingLeft: '15%',
        paddingRight: 16,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.8rem',
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
      }}
    >
      {/* Chofer (select con nombre de persona) */}
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Chofer:</label>
        <br /><br />
        <select
          className={`infoField${errors.idChofer ? ' infoField--error' : ''}`}
          value={form.idChofer || 0}
          onChange={(e) => handleChange('idChofer', Number(e.target.value) || 0)}
        >
          <option value={0} disabled>
            {loadingChoferes ? 'Cargando choferes…' : 'Seleccione un chofer'}
          </option>
          {choferes.map((c) => (
            <option key={c.idChofer} value={c.idChofer}>
              {labelChofer(c)}
            </option>
          ))}
        </select>
        {errors.idChofer && <small style={errorStyle}>{errors.idChofer}</small>}
        {errorChoferes && (
          <p
            style={{
              color: '#b91c1c',
              fontSize: '.8rem',
              marginTop: '.25rem',
            }}
          >
            {errorChoferes}
          </p>
        )}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Nombre de la excepción:</label>
        <br /><br />
        <input
          className={`infoField${errors.nombreExcepcion ? ' infoField--error' : ''}`}
          value={form.nombreExcepcion}
          onChange={(e) => handleChange('nombreExcepcion', e.target.value)}
          maxLength={30}
        />
        {errors.nombreExcepcion && <small style={errorStyle}>{errors.nombreExcepcion}</small>}
      </div>

      <div style={{ gridColumn: '1 / -1' }}>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Descripción:</label>
        <br /><br />
        <textarea
          className={`infoField${errors.descripcion ? ' infoField--error' : ''}`}
          style={{ minHeight: 80, resize: 'vertical' }}
          value={form.descripcion}
          onChange={(e) => handleChange('descripcion', e.target.value)}
          maxLength={200}
        />
        {errors.descripcion && <small style={errorStyle}>{errors.descripcion}</small>}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Fecha:</label>
        <br /><br />
        <input
          className={`infoField${errors.fechaInicio ? ' infoField--error' : ''}`}
          type="date"
          onKeyDown={(e) => e.preventDefault()}
          value={form.fechaInicio}
          onChange={(e) => handleChange('fechaInicio', e.target.value)}
        />
        {errors.fechaInicio && <small style={errorStyle}>{errors.fechaInicio}</small>}
      </div>

      {/* Aquí quitamos el input de fechaFin 👇
      (ya no se muestra en la UI) */}

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Hora inicio:</label>
        <br /><br />
        <input
          className={`infoField${errors.horaInicio ? ' infoField--error' : ''}`}
          type="time"
          onKeyDown={(e) => e.preventDefault()}
          value={form.horaInicio}
          onChange={(e) => handleChange('horaInicio', e.target.value)}
        />
        {errors.horaInicio && <small style={errorStyle}>{errors.horaInicio}</small>}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Hora fin:</label>
        <br /><br />
        <input
          className={`infoField${errors.horaFin ? ' infoField--error' : ''}`}
          type="time"
          onKeyDown={(e) => e.preventDefault()}
          value={form.horaFin}
          onChange={(e) => handleChange('horaFin', e.target.value)}
        />
        {errors.horaFin && <small style={errorStyle}>{errors.horaFin}</small>}
      </div>

      <div style={{ gridColumn: '1 / -1' }}>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Motivo:</label>
        <br /><br />
        <textarea
          className={`infoField${errors.motivo ? ' infoField--error' : ''}`}
          style={{ minHeight: 100, resize: 'vertical' }}
          value={form.motivo}
          onChange={(e) => handleChange('motivo', e.target.value)}
          maxLength={300}
        />
        {errors.motivo && <small style={errorStyle}>{errors.motivo}</small>}
      </div>

      {/* 👇 Campo "Activo" eliminado de la UI;
          form.activo sigue existiendo y se manda en onSubmit */}
    </div>
  );

  const Footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '.6rem',
        marginTop: '.8rem',
        paddingRight: 16,
        flex: '0 0 auto',
      }}
    >
      <button className="roles-btn" onClick={onClose}>Cancelar</button>
      <button
        className="btn-action"
        onClick={() => {
          if (validateAll()) {
            const payload = {
              ...form,
              fechaFin: form.fechaInicio,
            };
            onSubmit(payload, mode);
          }
        }}
        disabled={isSaveDisabled}
        style={{
          opacity: isSaveDisabled ? 0.5 : 1,
          cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        {mode === 'new' ? 'Guardar' : 'Actualizar'}
      </button>
    </div>
  );

  if (inline) {
    return (
      <div
        className="roles-inline-card"
        style={{ display: 'flex', flexDirection: 'column', gap: 0 }}
      >
        {Header}
        {Body}
        {Footer}
      </div>
    );
  }

  return (
    <div
      className="roles-confirm-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="roles-confirm-card"
        style={{
          width: 'min(720px, 96vw)',
          maxHeight: '85vh',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {Header}
        {Body}
        {Footer}
      </div>
    </div>
  );
}
